import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiEndpointsService } from 'src/app/core/services/api-endpoints.service';
import { ApiHttpService } from 'src/app/core/services/api-http.service';
import {
  QuarterlyReportsApiResponse,
  ReportsOverviewApiResponse,
} from 'src/app/core/models/reports';
export interface IAnnotationLogs {
  status: string;
  userId: string;
  filename: string;
  id?: string;
}

export type ReportsPaginatedSort = 'created' | 'updated';
export type ReportsPaginatedOrder = 'asc' | 'desc';

export interface IReportsPaginatedParams {
  limit: number;
  offset: number;
  type?: string;
  search?: string;
  sort?: ReportsPaginatedSort;
  order?: ReportsPaginatedOrder;
}
@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(
    private apiHttpService: ApiHttpService,
    private apiEndpointsService: ApiEndpointsService
  ) {}

  /** Legacy: full reports list (used by Reports page). */
  getAllReports(vehicleId: string) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(`vehicles/${vehicleId}/reports`, false)
    );
  }

  /** Paginated list with optional type filter (used by Reports New page). */
  getPaginatedReports(
    vehicleId: string,
    params: IReportsPaginatedParams
  ) {
    const query = new URLSearchParams();
    query.append('limit', params.limit.toString());
    query.append('offset', params.offset.toString());
    if (params.type) {
      query.append('type', params.type);
    }
    const search = params.search?.trim();
    if (search) {
      query.append('search', search);
    }
    if (params.sort) {
      query.append('sort', params.sort);
    }
    if (params.order) {
      query.append('order', params.order);
    }

    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/paginated?${query.toString()}`,
        false
      )
    );
  }

  /** Quarterly reports from S3 (used by New Reports — Quarterly Report type). */
  getQuarterlyReports(
    gpId: string,
    vehicleId: string
  ): Observable<QuarterlyReportsApiResponse> {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `gps/${gpId}/vehicles/${vehicleId}/quarterly-reports`,
        false
      )
    ) as unknown as Observable<QuarterlyReportsApiResponse>;
  }

  getReportsOverview(vehicleId: string): Observable<ReportsOverviewApiResponse> {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/overview`,
        false
      )
    ) as unknown as Observable<ReportsOverviewApiResponse>;
  }

  /** Download a quarterly report PDF (documentId from quarterly-reports list). */
  getQuarterlyReportDocument(
    gpId: string,
    vehicleId: string,
    documentId: string
  ) {
    const encodedDocumentId = encodeURIComponent(documentId);
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `gps/${gpId}/vehicles/${vehicleId}/quarterly-reports/${encodedDocumentId}`,
        false
      ),
      { responseType: 'arraybuffer' }
    );
  }

  // GET /vehicles/{vehicleId}/reports/{reportId}/limitedPartners
  getReportDetails(vehicleId: string, reportId: string) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/limitedPartners`,
        false
      )
    );
  }

  // GET /vehicles/{vehicleId}/reports/{reportId}/limitedPartners/{limitedPartnerId}/details
  getLPReportDetails(
    vehicleId: string,
    reportId: string,
    limitedPartnerId: string
  ) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/limitedPartners/${limitedPartnerId}/details`,
        false
      )
    );
  }

  // GET /vehicles/{vehicleId}/reports/{reportId}/files/{fileId}
  getFilebyId(vehicleId: string, reportId: string, fileId: number) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/files/${fileId}`,
        false
      ),
      { responseType: 'arraybuffer' }
    );
  }
  savepdfAnnotation(vehicleId: string, reportId: string, data: any) {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/annotations`,
        false
      ),
      data
    );
  }
  getReportAnnotation(vehicleId: string, reportId: string, filename: any) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/annotation?filename=${filename}`,
        false
      )
    );
  }
  saveUploadReportFile(
    vehicleId: string,
    reportId: string,
    formData: FormData
  ) {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/uploadfile`,
        false
      ),
      formData
    );
  }
  getDocumentFilebyFilePath(
    vehicleId: string,
    reportId: string,
    filePath: string
  ) {
    const filePathValue = encodeURIComponent(filePath);
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/filepath/${filePathValue}/getFile`,
        false
      )
    );
  }

  // get annotation logs
  getAnnotationLogStatus(vehicleId: string, reportId: string, fileName: any) {
    return this.apiHttpService.get(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/annotationsstatus/${fileName}`,
        false
      )
    );
  }
  // create AnnotationLogs
  createAnnotationLogs(
    vehicleId: string,
    reportId: string,
    data: IAnnotationLogs
  ) {
    return this.apiHttpService.post(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/anonnationlogs`,
        false
      ),
      data
    );
  }
  // update AnnotationLogs
  updateAnnotationLogs(
    vehicleId: string,
    reportId: string,
    data: IAnnotationLogs
  ) {
    return this.apiHttpService.put(
      this.apiEndpointsService.createUrl(
        `vehicles/${vehicleId}/reports/${reportId}/anonnationlogs`,
        false
      ),
      data
    );
  }
}
